import { type InvestmentAssetAllocation, type InvestmentHolding } from '@egw/api'
import { Collapse, Link, PieChart, type DefaultProps } from '@egw/components'
import { WEALTH_ROUTES } from '@egw/constants'
import { useTranslation } from '@egw/i18n'
import { buildBasicCurrency } from '@egw/utils'
import { ChevronRightIcon } from '@heroicons/react/16/solid'
import cx from 'classnames'
import { type FC } from 'react'
import { twMerge } from 'tailwind-merge'

export interface ApexHoldingsProps extends DefaultProps {
  data: InvestmentAssetAllocation
  alias: number
}

export const ApexHoldings: FC<ApexHoldingsProps> = ({
  // Common default properties for each component
  className,
  id,
  testId,
  ariaLabel,
  data,
  alias,
}) => {
  const { t } = useTranslation('features-investment')

  // Component base class names. Can be overriden by className property.
  const baseClass = cx('w-full')

  // Final class names
  const finalClasses = twMerge(baseClass, className)

  return (
    <div
      className={finalClasses}
      {...(testId ? { 'data-testid': testId } : {})}
      {...(id ? { id } : {})}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
    >
      <div className="w-full flex flex-col gap-[1.875rem]">
        {/* EQUITIES */}
        <Collapse
          className="flex items-center border-b border-gray-100 py-2"
          title={
            <div className="flex items-center md:gap-8 gap-4 w-full">
              <PieChart
                segmentOne={data.equities.percentage}
                segmentTwo={data.fixedIncome.percentage}
                segmentThree={data.cashEquivalent.percentage}
                segmentFour={data.other.percentage}
                highlightedSegment={1}
                showPercentage={false}
                className="h-[80px]"
              />
              <div className="flex flex-col md:flex-row w-full items-center">
                <div className="text-lg flex-1 text-header text-left font-semibold md:w-[30%] w-full">
                  {t('equity.title')}
                </div>
                <div className="flex-1 text-base text-left w-full">
                  {t('common.percent_formatter_of_portfolio', { percentage: data.equities.percentage })}
                </div>
              </div>
            </div>
          }
        >
          <div className="md:px-lg pl-3 pr-0 mt-1">
            {data.equities.holdings.length > 0 ? (
              data.equities.holdings
                .sort((a, b) => b.amount.amount - a.amount.amount)
                .slice(0, 3)
                .map(({ amount, symbol, name }: InvestmentHolding) => (
                  <Link
                    href={WEALTH_ROUTES.INVESTMENTS_ALLOCATION_HOLDINGS_DETAILS.path
                      .replace(':alias', alias.toString())
                      .replace(':assetAllocation', data.equities.__typename as string)
                      .replace(':symbol', symbol)}
                    key={symbol}
                    className="text-header font-normal"
                  >
                    <div className="flex flex-row justify-between py-sm border-b border-gray-100" key={symbol}>
                      <div className="typo-primary-base-semibold border-l-2 pl-xs border-action flex w-2/5 mr-md">
                        {symbol}
                      </div>
                      <div className="flex typo-primary-base items-left w-2/5 justify-left">{name}</div>
                      <div className="flex typo-primary-base w-1/5 self-end justify-end">
                        {buildBasicCurrency(amount) || '0.00'}
                      </div>
                    </div>
                  </Link>
                ))
            ) : (
              <p className="text-text-header typo-primary-xl-light p-lg text-center">{t('holdings.no_holdings')}</p>
            )}
            {data.equities.holdings.length > 3 ? (
              <Link
                href={WEALTH_ROUTES.INVESTMENTS_ALLOCATION_HOLDINGS_LIST.path
                  .replace(':alias', alias.toString())
                  .replace(':assetAllocation', data.equities.__typename as string)}
                data-testid="all-equities-holings_link"
                className="flex typo-primary-base text-text-body w-full py-sm border-b border-gray-100 justify-between items-center"
              >
                {t('holdings.see_all_equities')}
                <ChevronRightIcon className="h-6 w-6 fill-gray-200" />
              </Link>
            ) : null}
          </div>
        </Collapse>

        {/* FIXED INCOME */}
        <Collapse
          className="flex items-center border-b border-gray-100 py-2"
          title={
            <div className="flex items-center md:gap-8 gap-4 w-full">
              <PieChart
                segmentOne={data.equities.percentage}
                segmentTwo={data.fixedIncome.percentage}
                segmentThree={data.cashEquivalent.percentage}
                segmentFour={data.other.percentage}
                highlightedSegment={2}
                showPercentage={false}
                className="h-[80px]"
              />
              <div className="flex flex-col md:flex-row w-full items-center">
                <p className="text-lg flex-1 text-header text-left font-semibold md:w-[30%] w-full">
                  {t('fixed_income.title')}
                </p>
                <p className="flex-1 text-base text-left w-full">
                  {t('common.percent_formatter_of_portfolio', { percentage: data.fixedIncome.percentage })}
                </p>
              </div>
            </div>
          }
        >
          <div className="md:px-lg pl-3 pr-0 mt-1">
            {data.fixedIncome.holdings.length > 0 ? (
              data.fixedIncome.holdings
                .sort((a, b) => b.amount.amount - a.amount.amount)
                .slice(0, 3)
                .map(({ amount, symbol, name }: InvestmentHolding) => (
                  <Link
                    href={WEALTH_ROUTES.INVESTMENTS_ALLOCATION_HOLDINGS_DETAILS.path
                      .replace(':alias', alias.toString())
                      .replace(':assetAllocation', data.fixedIncome.__typename as string)
                      .replace(':symbol', symbol)}
                    key={symbol}
                    className="text-header font-normal"
                  >
                    <div className="flex flex-row justify-between py-sm border-b border-gray-100" key={symbol}>
                      <div className="typo-primary-base-semibold border-l-2 pl-xs border-action flex w-2/5 mr-md">
                        {symbol}
                      </div>
                      <div className="flex typo-primary-base items-left w-2/5 justify-left">{name}</div>
                      <div className="flex typo-primary-base w-1/5 self-end justify-end">
                        {buildBasicCurrency(amount)}
                      </div>
                    </div>
                  </Link>
                ))
            ) : (
              <p className="text-text-header typo-primary-xl-light p-lg text-center">{t('holdings.no_holdings')}</p>
            )}
            {data.fixedIncome.holdings.length > 3 ? (
              <Link
                href={WEALTH_ROUTES.INVESTMENTS_ALLOCATION_HOLDINGS_LIST.path
                  .replace(':alias', alias.toString())
                  .replace(':assetAllocation', data.fixedIncome.__typename as string)}
                data-testid="all-equities-holings_link"
                className="flex typo-primary-base text-text-body w-full py-sm border-b border-gray-100 justify-between items-center"
              >
                {t('holdings.see_all_fixed_income')}
                <ChevronRightIcon className="h-6 w-6 fill-gray-200" />
              </Link>
            ) : null}
          </div>
        </Collapse>

        {/* CASH EQUIVALENT */}
        <Collapse
          className="flex items-center border-b border-gray-100 py-2"
          title={
            <div className="flex w-full items-center md:gap-8 gap-4">
              <PieChart
                segmentOne={data.equities.percentage}
                segmentTwo={data.fixedIncome.percentage}
                segmentThree={data.cashEquivalent.percentage}
                segmentFour={data.other.percentage}
                highlightedSegment={3}
                showPercentage={false}
                className="h-[80px]"
              />
              <div className="flex flex-col md:flex-row w-full items-center">
                <div className="text-lg flex-1 text-header text-left font-semibold md:w-[30%] w-full">
                  {t('risk_return.cash_equivalent')}
                </div>
                <div className="flex-1 text-base text-left w-full">
                  {t('common.percent_formatter_of_portfolio', { percentage: data.cashEquivalent.percentage })}
                </div>
              </div>
            </div>
          }
        >
          <div className="md:px-lg pl-3 pr-0 mt-1">
            {data.cashEquivalent.holdings.length > 0 ? (
              data.cashEquivalent.holdings
                .sort((a, b) => b.amount.amount - a.amount.amount)
                .slice(0, 3)
                .map(({ amount, symbol, name }: InvestmentHolding) => (
                  <Link
                    href={WEALTH_ROUTES.INVESTMENTS_ALLOCATION_HOLDINGS_DETAILS.path
                      .replace(':alias', alias.toString())
                      .replace(':assetAllocation', data.cashEquivalent.__typename as string)
                      .replace(':symbol', symbol)}
                    key={symbol}
                    className="text-header font-normal"
                  >
                    <div className="flex flex-row justify-between py-sm border-b border-gray-100" key={symbol}>
                      <div className="typo-primary-base-semibold border-l-2 pl-xs border-action flex mr-md">{name}</div>
                      <div className="flex typo-primary-base self-end justify-end">{buildBasicCurrency(amount)}</div>
                    </div>
                  </Link>
                ))
            ) : (
              <p className="text-text-header typo-primary-xl-light p-lg text-center">{t('holdings.no_holdings')}</p>
            )}
            {data.cashEquivalent.holdings.length > 3 ? (
              <Link
                href={WEALTH_ROUTES.INVESTMENTS_ALLOCATION_HOLDINGS_LIST.path
                  .replace(':alias', alias.toString())
                  .replace(':assetAllocation', data.cashEquivalent.__typename as string)}
                data-testid="all-equities-holings_link"
                className="flex typo-primary-base text-text-body w-full py-sm border-b border-gray-100 justify-between items-center"
              >
                {t('holdings.see_all_cash_equivalent')}
                <ChevronRightIcon className="h-6 w-6 fill-gray-200" />
              </Link>
            ) : null}
          </div>
        </Collapse>

        {/* OTHER */}
        {data.other.percentage > 0 ? (
          <Collapse
            className="flex items-center border-b border-gray-100 py-2"
            title={
              <div className="flex w-full items-center md:gap-8 gap-4">
                <PieChart
                  segmentOne={data.equities.percentage}
                  segmentTwo={data.fixedIncome.percentage}
                  segmentThree={data.cashEquivalent.percentage}
                  segmentFour={data.other.percentage}
                  highlightedSegment={4}
                  showPercentage={false}
                  className="h-[80px]"
                />
                <div className="flex flex-col md:flex-row w-full items-center">
                  <div className="text-lg flex-1 text-header text-left font-semibold md:w-[30%] w-full">
                    {t('risk_return.uncategorized')}
                  </div>
                  <div className="flex-1 text-base text-left w-full">
                    {t('common.percent_formatter_of_portfolio', { percentage: data.other.percentage })}
                  </div>
                </div>
              </div>
            }
          >
            <div className="md:px-lg pl-3 pr-0 mt-1">
              {data.other.holdings.length > 0 ? (
                data.other.holdings
                  .sort((a, b) => b.amount.amount - a.amount.amount)
                  .slice(0, 3)
                  .map(({ amount, symbol, name }: InvestmentHolding) => (
                    <Link
                      href={WEALTH_ROUTES.INVESTMENTS_ALLOCATION_HOLDINGS_DETAILS.path
                        .replace(':alias', alias.toString())
                        .replace(':assetAllocation', data.other.__typename as string)
                        .replace(':symbol', symbol)}
                      key={symbol}
                      className="text-header font-normal"
                    >
                      <div className="flex flex-row justify-between py-sm border-b border-gray-100" key={symbol}>
                        <div className="typo-primary-base-semibold border-l-2 pl-xs border-action flex mr-md">
                          {name}
                        </div>
                        <div className="flex typo-primary-base self-end justify-end">{buildBasicCurrency(amount)}</div>
                      </div>
                    </Link>
                  ))
              ) : (
                <p className="text-text-header typo-primary-xl-light p-lg text-center">{t('holdings.no_holdings')}</p>
              )}
              {data.other.holdings.length > 3 ? (
                <Link
                  href={WEALTH_ROUTES.INVESTMENTS_ALLOCATION_HOLDINGS_LIST.path
                    .replace(':alias', alias.toString())
                    .replace(':assetAllocation', data.other.__typename as string)}
                  data-testid="all-equities-holings_link"
                  className="flex typo-primary-base text-text-body w-full py-sm border-b border-gray-100 justify-between items-center"
                >
                  {t('holdings.see_all_other')}
                  <ChevronRightIcon className="h-6 w-6 fill-gray-200" />
                </Link>
              ) : null}
            </div>
          </Collapse>
        ) : null}
      </div>
    </div>
  )
}
